import { Injectable, OnDestroy } from '@angular/core';
import { map, Observable, ReplaySubject, Subject, switchMap, takeUntil } from 'rxjs';
import { UserViewModel } from 'src/view-models/user-view-model';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserService implements OnDestroy {
  private readonly _authenticated$ = new ReplaySubject<UserViewModel | undefined>(1);
  private _authenticated?: UserViewModel;
  private readonly _impersonated$ = new ReplaySubject<UserViewModel | undefined>(1);
  private _impersonated?: UserViewModel;
  private readonly _current$ = new ReplaySubject<UserViewModel | undefined>(1);
  private _current?: UserViewModel;
  private _onDestroy$ = new Subject<void>();

  get valueChanges(): Observable<UserViewModel | undefined> {
    return this._current$.asObservable();
  }

  get value(): UserViewModel | undefined {
    return this._current;
  }

  get authenticated(): {
    value?: UserViewModel;
    valueChanges: Observable<UserViewModel | undefined>;
  } {
    return {
      value: this._authenticated,
      valueChanges: this._authenticated$.asObservable(),
    };
  }

  get impersonated(): {
    value?: UserViewModel;
    valueChanges: Observable<UserViewModel | undefined>;
  } {
    return {
      value: this._impersonated,
      valueChanges: this._impersonated$.asObservable(),
    };
  }

  constructor() {
    this._authenticated$
      .pipe(
        switchMap((authenticated) =>
          this._impersonated$.pipe(map((impersonated) => impersonated ?? authenticated)),
        ),
        takeUntil(this._onDestroy$),
      )
      .subscribe((current) => {
        this._current = current;
        this._current$.next(current);
      });
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public _setAuthenticatedUser(user: UserViewModel | undefined) {
    this._authenticated = user;
    this._authenticated$.next(user);
  }

  public _setImpersonatedUser(user: UserViewModel | undefined) {
    this._impersonated = user;
    this._impersonated$.next(user);
  }
}
